import { useState } from "react";
import {
  EngravingPosition,
  ENGRAVING_POSITIONS,
  TYPOGRAPHY_OPTIONS,
  ENGRAVING_COLORS,
  MAX_ENGRAVING_LENGTH,
} from "@/types/agendaCustomization";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";

interface EngravingSectionProps {
  hasEngraving: boolean;
  engravingText?: string;
  engravingPosition?: EngravingPosition;
  engravingTypography?: string;
  engravingColor?: string;
  hasLogo?: boolean;
  onEngravingToggle: (enabled: boolean) => void;
  onEngravingTextChange: (text: string) => void;
  onPositionChange: (position: EngravingPosition) => void;
  onTypographyChange: (typography: string) => void;
  onColorChange: (color: string) => void;
  onLogoToggle: (enabled: boolean) => void;
  onLogoUpload: (file: File) => void;
}

export const EngravingSection = ({
  hasEngraving,
  engravingText = "",
  engravingPosition = "centro",
  engravingTypography,
  engravingColor,
  hasLogo = false,
  onEngravingToggle,
  onEngravingTextChange,
  onPositionChange,
  onTypographyChange,
  onColorChange,
  onLogoToggle,
  onLogoUpload,
}: EngravingSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(hasEngraving);

  const handleToggle = () => {
    const newState = !hasEngraving;
    onEngravingToggle(newState);
    setIsExpanded(newState);
  };

  const remainingChars = MAX_ENGRAVING_LENGTH - engravingText.length;

  return (
    <div className="space-y-3">
      {/* Botón disparador */}
      <button
        onClick={handleToggle}
        className={cn(
          "w-full border rounded-lg p-4 transition-all text-left flex items-center justify-between",
          hasEngraving
            ? "border-primary bg-primary/5 shadow-md"
            : "border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
        )}
      >
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          ¡Quiero añadir un grabado a mis cubiertas!
        </span>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {/* Contenido expandible */}
      {isExpanded && hasEngraving && (
        <div className="space-y-4 border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50/50 dark:bg-gray-800/50">
          {/* Selector de posición */}
          <div className="space-y-2">
            <label className="text-xs font-serif text-gray-600 dark:text-gray-400">
              Posición del Grabado
            </label>
            <div className="grid grid-cols-4 gap-2">
              {ENGRAVING_POSITIONS.map((pos) => (
                <button
                  key={pos.value}
                  onClick={() => onPositionChange(pos.value)}
                  className={cn(
                    "border rounded px-3 py-2 text-xs transition-all hover:shadow-md",
                    engravingPosition === pos.value
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
                  )}
                >
                  {pos.label}
                </button>
              ))}
            </div>
          </div>

          {/* Input de texto */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="engraving-text" className="text-xs font-serif text-gray-600 dark:text-gray-400">
                Texto del Grabado
              </Label>
              <span
                className={cn(
                  "text-xs",
                  remainingChars < 10 ? "text-orange-600" : "text-gray-400"
                )}
              >
                {remainingChars} caracteres restantes
              </span>
            </div>
            <Input
              id="engraving-text"
              type="text"
              value={engravingText}
              onChange={(e) => {
                if (e.target.value.length <= MAX_ENGRAVING_LENGTH) {
                  onEngravingTextChange(e.target.value);
                }
              }}
              placeholder="Escribe tu texto aquí..."
              maxLength={MAX_ENGRAVING_LENGTH}
              className="border-gray-300 dark:border-gray-600"
            />
          </div>

          {/* Selector de tipografía */}
          <div className="space-y-2">
            <label className="text-xs font-serif text-gray-600 dark:text-gray-400">
              Tipografía
            </label>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
              {TYPOGRAPHY_OPTIONS.map((typo) => (
                <Badge
                  key={typo.id}
                  variant={engravingTypography === typo.id ? "default" : "outline"}
                  className={cn(
                    "cursor-pointer justify-center py-1.5 px-3 transition-all hover:shadow-md",
                    engravingTypography === typo.id
                      ? "bg-primary text-primary-foreground"
                      : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
                  )}
                  onClick={() => onTypographyChange(typo.id)}
                  style={
                    engravingTypography === typo.id
                      ? { fontFamily: typo.fontFamily }
                      : undefined
                  }
                >
                  <span className="text-xs">{typo.name}</span>
                </Badge>
              ))}
            </div>
          </div>

          {/* Selector de color */}
          <div className="space-y-2">
            <label className="text-xs font-serif text-gray-600 dark:text-gray-400">
              Color del Grabado
            </label>
            <div className="grid grid-cols-8 md:grid-cols-12 gap-2">
              {ENGRAVING_COLORS.map((color) => (
                <button
                  key={color.id}
                  onClick={() => onColorChange(color.id)}
                  title={color.name}
                  className={cn(
                    "w-6 h-6 rounded-full cursor-pointer transition-all hover:scale-125",
                    engravingColor === color.id && "ring-2 ring-primary ring-offset-2"
                  )}
                  style={{ backgroundColor: color.hex }}
                >
                  <span className="sr-only">{color.name}</span>
                </button>
              ))}
            </div>
            {engravingColor && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Color seleccionado:{" "}
                <span className="font-medium">
                  {ENGRAVING_COLORS.find((c) => c.id === engravingColor)?.name}
                </span>
              </p>
            )}
          </div>

          {/* Checkbox y upload de logo */}
          <div className="space-y-3 border-t pt-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="logo-upload"
                checked={hasLogo}
                onCheckedChange={(checked) => onLogoToggle(checked as boolean)}
              />
              <Label
                htmlFor="logo-upload"
                className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
              >
                Quiero subir mi logo (+$50 MXN vectorización)
              </Label>
            </div>

            {hasLogo && (
              <div>
                <Label htmlFor="logo-file" className="text-xs text-gray-600 dark:text-gray-400">
                  Archivo del logo
                </Label>
                <Input
                  id="logo-file"
                  type="file"
                  accept="image/*,.svg"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onLogoUpload(file);
                  }}
                  className="mt-1"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
